import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Palette, ImageIcon, Briefcase, Music2, Brush, Clock, ArrowRight, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import PageTransition from '../../components/common/PageTransition';
import SEOHead from '../../components/common/SEOHead';
import HeroBanner from '../../components/common/HeroBanner';
import GalleryGrid from '../../components/gallery/GalleryGrid';
import AudioPlayer from '../../components/common/AudioPlayer';
import { artworks, getFeaturedArtworks } from '../../data/artworks';
import { projects } from '../../data/projects';
import { sounds } from '../../data/sounds';
import { heroArtSocials, getSocialById } from '../../data/socialLinks';
import SocialIcon from '../../components/common/SocialIcon';
import { Artwork } from '../../types';

type ArtTab = 'gallery' | 'projects' | 'music' | 'commissions';

const ArtHome: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.hash.replace('#', '');
  const validTabs: ArtTab[] = ['gallery', 'projects', 'music', 'commissions'];

  const [activeTab, setActiveTab] = useState<ArtTab>(
    validTabs.includes(initialTab as ArtTab) ? (initialTab as ArtTab) : 'gallery'
  );

  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  const featuredArtworks = getFeaturedArtworks();

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current && carouselRef.current.firstElementChild) {
      // scrollAmount is the width of one card + the gap (16px)
      const scrollAmount = (carouselRef.current.firstElementChild as HTMLElement).offsetWidth + 16;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const tabs: { key: ArtTab; labelKey: string; icon: React.ReactNode }[] = [
    { key: 'gallery', labelKey: 'art.home.tabs.gallery', icon: <ImageIcon className="w-4 h-4" /> },
    { key: 'projects', labelKey: 'art.home.tabs.projects', icon: <Briefcase className="w-4 h-4" /> },
    { key: 'music', labelKey: 'art.home.tabs.music', icon: <Music2 className="w-4 h-4" /> },
    { key: 'commissions', labelKey: 'art.home.tabs.commissions', icon: <Brush className="w-4 h-4" /> },
  ];

  return (
    <PageTransition>
      <SEOHead title={t('nav.art')} />
      <div className="space-y-8">
        {/* Hero Banner (compact) */}
        <HeroBanner
          title={t('art.home.title')}
          subtitle={t('home.artCard.subtitle')}
          variant="art"
          imageSrc="/img/IMG_20260327_081744.jpg"
          ctaText={t('art.home.viewGallery')}
          ctaLink="/art/gallery"
        />

        {/* Social Links */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          {heroArtSocials.map(id => {
            const social = getSocialById(id);
            return social ? <SocialIcon key={social.id} social={social} size="lg" /> : null;
          })}
        </div>

        {/* Tab Navigation */}
        <div className="relative">
          <div className="flex overflow-x-auto gap-1 p-1 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${activeTab === tab.key
                    ? 'bg-brand-amber text-slate-900 shadow-md shadow-brand-amber/20'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
              >
                {tab.icon}
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px]">
          {/* === GALLERY TAB === */}
          {activeTab === 'gallery' && (
            <section className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-2">
                <Palette className="w-6 h-6 text-brand-amber" />
                <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                  {t('art.home.featured')}
                </h2>
              </div>

              <div className="relative group">
                <button
                  onClick={() => scrollCarousel('left')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-black/60 text-white rounded-full shadow-lg transition-opacity hover:bg-black/80 backdrop-blur-sm"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <div
                  ref={carouselRef}
                  className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 scrollbar-hide"
                >
                  {featuredArtworks.map(artwork => (
                    <div
                      key={artwork.id}
                      className="min-w-[85%] sm:min-w-[calc(50%-0.5rem)] md:min-w-[calc(33.333%-0.67rem)] snap-center shrink-0 cursor-pointer relative rounded-xl overflow-hidden art-card border-white/5"
                      onClick={() => navigate('/art/gallery', { state: { selectedArtworkId: artwork.id } })}
                    >
                      <img
                        src={artwork.imageUrl}
                        alt={t(`artworks.${artwork.id}.title`)}
                        className="w-full h-64 object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent md:opacity-0 md:hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-white font-bold text-lg truncate pr-2">{t(`artworks.${artwork.id}.title`)}</h3>
                          <span className="text-xs font-semibold text-white/70 whitespace-nowrap">{artwork.date}</span>
                        </div>
                        <div className="flex gap-2 mt-1">
                          {artwork.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="text-[10px] uppercase font-bold text-brand-amber bg-black/50 px-1.5 py-0.5 rounded backdrop-blur-sm">
                              #{t(`tags.${tag}`, { defaultValue: tag })}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => scrollCarousel('right')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-black/60 text-white rounded-full shadow-lg transition-opacity hover:bg-black/80 backdrop-blur-sm"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              <div className="text-center pt-4">
                <Link
                  to="/art/gallery"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-amber/10 hover:bg-brand-amber/20 border border-brand-amber/20 text-brand-amber font-bold transition-all hover:scale-[1.02]"
                >
                  {t('art.home.viewAllGallery')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Shop Card */}
              <div className="art-card p-6 rounded-2xl border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6 group">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-brand-amber/10 rounded-full text-brand-amber shrink-0 transition-transform group-hover:scale-110">
                    <Heart className="w-6 h-6 fill-current" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-brand-amber mb-1">{t('about.shopCard.title')}</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                      {t('about.shopCard.desc')}
                    </p>
                  </div>
                </div>
                <a
                  href="https://shop.kynothan.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-amber hover:bg-amber-400 text-slate-900 text-sm font-bold hover:scale-105 transition-transform shadow-md"
                >
                  <span>{t('about.shopCard.cta')}</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

            </section>
          )}

          {/* === PROJECTS TAB === */}
          {activeTab === 'projects' && (
            <section className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-brand-amber" />
                <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                  {t('art.projects.title')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {projects.map(project => (
                  <Link
                    key={project.id}
                    to={`/art/projects/${project.id}`}
                    state={{ from: 'arthome' }}
                    className="block group"
                  >
                    <div className="art-card rounded-2xl overflow-hidden h-full hover:border-brand-amber/30 transition-all">
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={project.imageUrl}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                        <span className="absolute bottom-3 left-3 text-xs font-semibold text-white/80">
                          {project.date}
                        </span>
                      </div>
                      <div className="p-5 space-y-3">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-brand-amber transition-colors">
                          {t(`projects.${project.id.replace('project-', '')}.title`, { defaultValue: project.title })}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                          {t(project.description, { defaultValue: project.description })}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {project.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-md text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase"
                            >
                              {t(`tags.${tag}`, { defaultValue: tag })}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center pt-4 space-y-6">
                <Link
                  to="/art/projects"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-amber/10 hover:bg-brand-amber/20 border border-brand-amber/20 text-brand-amber font-bold transition-all hover:scale-[1.02]"
                >
                  {t('art.home.viewAllProjects')}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl mx-auto italic">
                  {t('art.home.missingProjectsNote')}
                </p>
              </div>
            </section>
          )}

          {/* === MUSIC TAB === */}
          {activeTab === 'music' && (
            <section className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-2">
                <Music2 className="w-6 h-6 text-brand-amber" />
                <div>
                  <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                    {t('art.music.title')}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('art.music.description')}
                  </p>
                </div>
              </div>

              {/* Tracks List */}
              <div className="space-y-3">
                {sounds.map(track => (
                  <AudioPlayer key={track.id} track={track} />
                ))}
              </div>

              {sounds.length === 0 && (
                <div className="text-center py-12 art-card rounded-2xl">
                  <Music2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500">{t('art.music.description')}</p>
                </div>
              )}

              {/* Listen On Links */}
              <div className="art-card rounded-2xl p-5 space-y-3">
                <h3 className="font-bold text-slate-900 dark:text-white text-sm">
                  {t('art.music.listenOn')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://open.spotify.com/artist/3n6Rbc3WXl82wNeUpoBqrV"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1DB954]/10 hover:bg-[#1DB954]/20 border border-[#1DB954]/20 text-[#1DB954] font-semibold text-sm transition-all hover:scale-[1.02]"
                  >
                    <img src="/img/spotify.svg" alt="Spotify" className="w-4 h-4" />
                    Spotify
                  </a>
                  <a
                    href="https://soundcloud.com/kynothan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF5500]/10 hover:bg-[#FF5500]/20 border border-[#FF5500]/20 text-[#FF5500] font-semibold text-sm transition-all hover:scale-[1.02]"
                  >
                    <img src="/img/soundcloud.svg" alt="SoundCloud" className="w-4 h-4" />
                    SoundCloud
                  </a>
                </div>
              </div>
            </section>
          )}

          {/* === COMMISSIONS TAB === */}
          {activeTab === 'commissions' && (
            <section className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-2">
                <Brush className="w-6 h-6 text-brand-amber" />
                <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                  {t('art.commissions.title')}
                </h2>
              </div>

              <p className="text-slate-600 dark:text-slate-400">
                {t('art.commissions.description')}
              </p>

              {/* Status Alert */}
              <div className="bg-brand-amber/10 border border-brand-amber/30 rounded-xl p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-brand-amber shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-brand-amber">
                    {t('art.commissions.status')} : {t('art.commissions.statusOpen')}
                  </h4>
                  <p className="text-sm text-brand-amber/80">
                    {t('art.commissions.statusDesc')}
                  </p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-brand-amber hover:bg-amber-400 text-slate-900 font-bold text-lg transition-all shadow-lg shadow-brand-amber/20 hover:scale-[1.02]"
                >
                  {t('art.commissions.requestQuote')}
                </Link>
                <Link
                  to="/art/commissions"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-brand-amber/10 hover:bg-brand-amber/20 border border-brand-amber/20 text-brand-amber font-bold transition-all hover:scale-[1.02]"
                >
                  {t('art.commissions.learnMore')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </PageTransition>
  );
};

export default ArtHome;
